import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '../../Icon/Icon';

/**
 * ConversationHistory - History panel component for Gigabot
 *
 * Props:
 * - isOpen: boolean - Whether the history panel is open
 * - conversations: array - List of conversation objects
 * - activeConversationId: string - Currently active conversation ID
 * - isLoading: boolean - Whether conversations are loading
 * - onSelectConversation: function - Callback when a conversation is selected
 * - onDeleteConversation: function - Callback when a conversation is deleted
 * - onNewConversation: function - Callback to start a new conversation
 * - formatRelativeTime: function - Utility to format relative timestamps
 */
export const ConversationHistory = forwardRef(
  (
    {
      isOpen,
      conversations = [],
      activeConversationId,
      isLoading = false,
      onSelectConversation,
      onDeleteConversation,
      onNewConversation,
      formatRelativeTime,
    },
    ref
  ) => {
    const handleDelete = (e, conv) => {
      e.stopPropagation(); // Prevent triggering onSelectConversation
      if (onDeleteConversation) {
        onDeleteConversation(conv);
      }
    };
    return (
      <div
        className={`gigabot-history-wrapper ${isOpen ? 'open' : ''}`}
        ref={ref}
      >
        <div className='gigabot-history-panel'>
          <div className='history-panel-header'>
            <span>Recent Conversations</span>
            <button
              className='new-chat-btn'
              onClick={onNewConversation}
              title='Start new conversation'
            >
              <Icon name='Add_Plus' width={14} height={14} />
              <span>New</span>
            </button>
          </div>
          <div className='history-panel-content'>
            {isLoading ? (
              <div className='history-loading'>
                <div className='loading-spinner small'></div>
                <span>Loading...</span>
              </div>
            ) : conversations.length === 0 ? (
              <div className='history-empty'>
                <span>No previous conversations</span>
              </div>
            ) : (
              <div className='history-list'>
                {conversations.map((conv) => (
                  <button
                    key={conv.conversation_id}
                    className={`history-item ${
                      activeConversationId === conv.conversation_id
                        ? 'active'
                        : ''
                    }`}
                    onClick={() => onSelectConversation(conv)}
                  >
                    <div className='history-item-content'>
                      <span className='history-item-title'>
                        {conv.title || conv.summary || 'Untitled conversation'}
                      </span>
                      <span className='history-item-meta'>
                        {conv.total_messages || 0} messages ·{' '}
                        {formatRelativeTime(
                          conv.last_activity_at || conv.created_at
                        )}
                      </span>
                    </div>
                    <div className='history-item-actions'>
                      {conv.is_pinned && (
                        <Icon
                          name='Map_Pin'
                          width={12}
                          height={12}
                          className='pin-icon'
                        />
                      )}
                      <button
                        className='delete-btn'
                        onClick={(e) => handleDelete(e, conv)}
                        title='Delete conversation'
                        aria-label='Delete conversation'
                      >
                        <Icon name='Trash_Empty' width={14} height={14} />
                      </button>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

ConversationHistory.displayName = 'ConversationHistory';

ConversationHistory.propTypes = {
  isOpen: PropTypes.bool,
  conversations: PropTypes.arrayOf(
    PropTypes.shape({
      conversation_id: PropTypes.string.isRequired,
      title: PropTypes.string,
      summary: PropTypes.string,
      total_messages: PropTypes.number,
      last_activity_at: PropTypes.string,
      created_at: PropTypes.string,
      is_pinned: PropTypes.bool,
    })
  ),
  activeConversationId: PropTypes.string,
  isLoading: PropTypes.bool,
  onSelectConversation: PropTypes.func.isRequired,
  onDeleteConversation: PropTypes.func,
  onNewConversation: PropTypes.func.isRequired,
  formatRelativeTime: PropTypes.func.isRequired,
};

export default ConversationHistory;
